// src/components/RobotResultsPage.tsx
import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";

// واجهة صف البيانات لليوم
interface TradeDay {
  day: string;
  date: string;
  total: number;
  wins: number;
  losses: number;
  successRate: number;
}

// أيام الأسبوع باللغة العربية
const daysOfWeek = ["الأحد", "الإثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];

/**
 * simpleHash: ينتج عدد صحيح 32-bit من سترينج (نستخدمه كـ seed)
 */
const simpleHash = (s: string): number => {
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    const chr = s.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0;
  }
  return Math.abs(hash);
};

/**
 * mulberry32: مُولد أعداد عشوائية ثابت (deterministic) من seed صحيح
 * يرجع رقم في المجال [0,1)
 */
const mulberry32 = (a: number) => {
  return function () {
    a |= 0;
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/**
 * توليد صف بيانات ليوم معين.
 */
const generateRandomDayData = (date: Date): TradeDay => {
  // seed ثابت لكل يوم على شكل YYYY-MM-DD
  const seedBase = date.toISOString().slice(0, 10);
  const seed = simpleHash(seedBase);

  const rnd = mulberry32(seed);
  const rnd2 = mulberry32(seed + 1);

  // نسبة الربح 85.0 - 95.0 (واحد عشري)
  const successRate = +(85 + Math.floor(rnd() * 1000) / 1000 * 10).toFixed(1);

  // عدد الصفقات 200 - 300 (شامل)
  const total = Math.floor(200 + Math.floor(rnd2() * 1000) / 1000 * 101);

  // ضمان البقاء ضمن الحدود
  const boundedTotal = Math.max(200, Math.min(300, total));
  const wins = Math.round((successRate / 100) * boundedTotal);
  const losses = boundedTotal - wins;

  // تنسيق التاريخ بالعربية (مثل ٢‏/١١‏/٢٠٢٥)
  // يتم استخدام تنسيق عربي للتواريخ ليناسب الاتجاه RTL
  const formattedDate = date.toLocaleDateString("ar-EG", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
  }).replace(/\u200f/g, "").replace(/\u200e/g, "");

  return {
    day: daysOfWeek[date.getDay()],
    date: formattedDate,
    total: boundedTotal,
    wins,
    losses,
    successRate,
  };
};

/**
 * يحصل آخر 7 أيام (الأقدم أولاً، الأحدث في الآخر)
 */
const getWeekDataForToday = (referenceDate = new Date()): TradeDay[] => {
  const data: TradeDay[] = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(referenceDate);
    d.setDate(referenceDate.getDate() - i);
    data.push(generateRandomDayData(d));
  }
  return data;
};

// تم تعديل أحجام الخطوط (text-sm/md -> text-xs/base) لتبدو أفضل على الهواتف
const Card = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <motion.div
    whileHover={{ scale: 1.03 }}
    transition={{ type: "spring", stiffness: 200 }}
    className="bg-[rgba(13,19,43,0.7)] border border-[rgba(34,255,155,0.15)] p-4 sm:p-5 rounded-xl sm:rounded-2xl backdrop-blur-xl shadow-[0_0_25px_rgba(34,255,155,0.08)] hover:shadow-[0_0_40px_rgba(34,255,155,0.15)] transition-all"
  >
    <h3 className="text-base sm:text-lg font-bold text-[#22ff9b] mb-2 text-center md:text-start">{title}</h3>
    <div className="text-[#a3f9d4] text-xs sm:text-sm space-y-1 text-center md:text-start">{children}</div>
  </motion.div>
);

export default function RobotResultsPage() {
  const [weekData, setWeekData] = useState<TradeDay[]>([]);
  const [weekRange, setWeekRange] = useState("");

  const updateWeekRange = useCallback((data: TradeDay[]) => {
    if (!data || data.length === 0) {
      setWeekRange("");
      return;
    }
    const start = data[0].date;
    const end = data[data.length - 1].date;
    setWeekRange(`${start} ➜ ${end}`);
  }, []);

  const refreshData = useCallback(() => {
    const data = getWeekDataForToday(new Date());
    setWeekData(data);
    updateWeekRange(data);
  }, [updateWeekRange]);

  useEffect(() => {
    refreshData();

    const hourly = setInterval(() => {
      refreshData();
    }, 60 * 60 * 1000); // كل ساعة

    return () => clearInterval(hourly);
  }, [refreshData]);

  // إجماليات الأسبوع
  const totalTrades = weekData.reduce((sum, d) => sum + d.total, 0);
  const totalWins = weekData.reduce((sum, d) => sum + d.wins, 0);
  const totalLosses = totalTrades - totalWins;
  const overallSuccessRate = totalTrades > 0 ? ((totalWins / totalTrades) * 100).toFixed(2) : "0.00";

  return (
    // تم ضبط p-4/p-6 إلى p-3/p-6
    <div className="text-white p-3 md:p-6 animate-fadeIn space-y-6 md:space-y-10" dir="rtl">
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-xl md:text-4xl font-black text-center text-[#22ff9b] mb-4 md:mb-8 drop-shadow-[0_0_10px_rgba(34,255,155,0.6)]"
      >
        🤖 أداء الروبوت خلال هذا الأسبوع
      </motion.h2>

      {/* ملخصات سريعة: تم تعديل grid-cols-2 md:grid-cols-4 لتناسب الهواتف */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 bg-[rgba(13,19,43,0.8)] p-3 md:p-4 rounded-lg md:rounded-xl border border-[rgba(34,255,155,0.2)]"
      >
        <div className="text-center p-1 border-r border-r-[rgba(34,255,155,0.1)] last:border-r-0 md:last:border-r">
          <p className="text-xs text-[#a3f9d4] opacity-70">إجمالي الصفقات</p>
          <p className="text-lg md:text-2xl font-black text-white">{totalTrades}</p>
        </div>
        <div className="text-center p-1">
          <p className="text-xs text-[#a3f9d4] opacity-70">إجمالي الربح</p>
          <p className="text-lg md:text-2xl font-black text-green-400">{totalWins}</p>
        </div>
        {/* صف جديد للهواتف (لتجنب ضيق المساحة) ولكن يظهران بجانب بعضهما على الأجهزة اللوحية فما فوق */}
        <div className="text-center p-1 border-r border-r-[rgba(34,255,155,0.1)] last:border-r-0 md:border-r">
          <p className="text-xs text-[#a3f9d4] opacity-70">إجمالي الخسارة</p>
          <p className="text-lg md:text-2xl font-black text-red-400">{totalLosses}</p>
        </div>
        <div className="text-center p-1">
          <p className="text-xs text-[#a3f9d4] opacity-70">متوسط النجاح</p>
          <p className="text-lg md:text-2xl font-black text-[#22ff9b]">{overallSuccessRate}%</p>
        </div>
      </motion.div>

      {/* جدول البيانات: تم استخدام text-xs لتصغير الخط على الهواتف */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="rounded-xl md:rounded-2xl border border-[rgba(34,255,155,0.15)] backdrop-blur-md shadow-[0_0_25px_rgba(34,255,155,0.08)] overflow-x-auto"
      >
        <table className="min-w-full text-center text-[#c2ffe2] border-collapse text-xs sm:text-sm">
          <thead className="bg-[rgba(34,255,155,0.1)] text-[#22ff9b]">
            <tr>
              {/* تم تقليل الـ padding ليناسب شاشات الهاتف */}
              <th className="p-2 sm:p-3">اليوم</th>
              <th className="p-2 sm:p-3">التاريخ</th>
              <th className="p-2 sm:p-3">الصفقات</th>
              <th className="p-2 sm:p-3">الرابحة</th>
              <th className="p-2 sm:p-3">الخاسرة</th>
              <th className="p-2 sm:p-3">الربح (%)</th>
            </tr>
          </thead>
          <tbody>
            {weekData.map((day, index) => (
              <motion.tr
                key={index}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.06 }}
                className="border-b border-[rgba(34,255,155,0.1)] hover:bg-[rgba(34,255,155,0.05)] transition-all"
              >
                <td className="p-2 sm:p-3 font-bold text-[#22ff9b]">{day.day}</td>
                <td className="p-2 sm:p-3">{day.date}</td>
                <td className="p-2 sm:p-3">{day.total}</td>
                <td className="p-2 sm:p-3 text-green-400">{day.wins}</td>
                <td className="p-2 sm:p-3 text-red-400">{day.losses}</td>
                <td className="p-2 sm:p-3 font-semibold text-[#22ff9b]">{day.successRate}%</td>
              </motion.tr>
            ))}
          </tbody>
          <tfoot className="bg-[rgba(34,255,155,0.08)] text-xs sm:text-sm text-[#7fffd4]">
            <tr>
              <td colSpan={6} className="p-2 sm:p-3 text-center whitespace-nowrap">
                نتائج الأسبوع من{" "}
                <span className="text-[#22ff9b] font-bold">{weekRange}</span>
              </td>
            </tr>
          </tfoot>
        </table>
      </motion.div>

      {/* بطاقات الميزات: تم تغيير الفواصل الزمنية والـ padding */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6 mt-6 md:mt-10">
        <Card title="⚡ يدعم جميع المنصات">
          <p>الروبوت متوافق مع: <strong>MetaTrader</strong>، <strong>Binance</strong>، <strong>OKX</strong>، و<strong>Bybit</strong>.</p>
          <p>إعدادات سهلة وسريعة لكل مستخدم.</p>
        </Card>

        <Card title="🌍 متاح لجميع الدول">
          <p>يعمل بدون قيود جغرافية أو VPN.</p>
          <p>يدعم أكثر من 120 دولة حول العالم.</p>
        </Card>

        <Card title="🕐 توقيت متزامن عالميًا">
          <p>كل الصفقات تعمل بتوقيت منطقتك المحلية تلقائيًا.</p>
          <p>التحديث يتحقق كل ساعة ويتغير فعليًا يوميًا عند تغير التاريخ.</p>
        </Card>
      </div>

      <p className="text-center text-xs text-[#7fffd4]/70 mt-4 md:mt-8 leading-relaxed">
        ⚠️ النتائج الظاهرة في هذه الضفحه مرتبطه بـ API الروبوت وتتحدث باستمرار.
      </p>

      {/* زر الاشتراك: تم تعديل حجم الخط والـ padding */}
      <div className="flex justify-center mt-4 md:mt-8">
        <a
          href="https://t.me/m/TAvESO-WMDFk"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-400 to-teal-500 text-slate-900 py-3 px-6 rounded-lg font-black uppercase tracking-wider text-sm shadow-lg shadow-emerald-400/30 hover:shadow-xl hover:shadow-emerald-400/50 transition-all duration-300 hover:scale-105"
        >
          🤖 اشترك الآن بالروبوت
        </a>
      </div>
    </div>
  );
}